import axios from 'axios';

import {
  clearAuthTokens,
  getAccessToken,
  getRefreshToken,
  setAuthTokens,
} from './authStorage';

const apiBaseURL = (process.env.REACT_APP_API_URL || '/api/').replace(/\/?$/, '/');

const apiClient = axios.create({
  baseURL: apiBaseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const refreshClient = axios.create({
  baseURL: apiBaseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise = null;

function isRefreshRequest(url = '') {
  return String(url).includes('/token/refresh/');
}

function isTokenRequest(url = '') {
  return String(url).includes('/token/');
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available.');
  }

  if (!refreshPromise) {
    refreshPromise = refreshClient
      .post('/token/refresh/', { refresh: refreshToken })
      .then((response) => {
        const nextAccessToken = response?.data?.access;
        const nextRefreshToken = response?.data?.refresh || refreshToken;

        if (!nextAccessToken) {
          throw new Error('Refresh response did not include a new access token.');
        }

        setAuthTokens({
          access: nextAccessToken,
          refresh: nextRefreshToken,
        });

        return nextAccessToken;
      })
      .catch((error) => {
        clearAuthTokens();
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

apiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const responseStatus = error?.response?.status;

    if (!originalRequest || responseStatus !== 401) {
      return Promise.reject(error);
    }

    if (isTokenRequest(originalRequest.url) && !isRefreshRequest(originalRequest.url)) {
      return Promise.reject(error);
    }

    if (originalRequest._retry || isRefreshRequest(originalRequest.url)) {
      clearAuthTokens();
      return Promise.reject(error);
    }

    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    if (!accessToken || !refreshToken) {
      clearAuthTokens();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const nextAccessToken = await refreshAccessToken();
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`;
      return apiClient.request(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }
);

export default apiClient;
